import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Space, Row, Col, Tag, Typography, Alert, Select } from "antd";
import {
  FileExcelOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  WifiOutlined,
  PictureOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import PageHeader from "./common/PageHeader";
import StatCard from "./common/StatCard";
import {
  getOfflineDevicesReport,
  getImageFreshnessReport,
  getAssignableFieldUsers,
  assignOfflineTickets,
} from "../network/api";
import { TICKET_STATUS_TAG_COLOR, TICKET_STATUS_LABEL } from "../constants/tickets";
import { notify, serverError } from "../utils/notify";

const { Text } = Typography;

// Coerce an API date string (or null) to a JS Date so SheetJS writes a real date cell.
const toDate = (v) => (v ? new Date(v) : "");
const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");
// YYYY-MM-DD for filenames, from the report's generatedAt (falls back to today).
const stamp = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toISOString().slice(0, 10);
};

// Humanize an offline duration in minutes -> "3d 4h" / "2h 10m" / "8m".
const humanizeMins = (mins) => {
  if (mins == null || Number.isNaN(mins)) return null;
  if (mins < 1) return "just now";
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = Math.floor(mins % 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Build a worksheet from ordered column defs so headers, order and widths are explicit.
// A column may set { link: (row) => url } to turn its cell into a clickable hyperlink.
function sheetFrom(columns, rows) {
  const header = columns.map((c) => c.header);
  const body = rows.map((r) => columns.map((c) => r[c.key]));
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  ws["!cols"] = columns.map((c) => ({ wch: c.width || 16 }));
  columns.forEach((c, ci) => {
    if (!c.link) return;
    rows.forEach((r, ri) => {
      const url = c.link(r);
      if (!url) return;
      const addr = XLSX.utils.encode_cell({ c: ci, r: ri + 1 }); // +1 skips the header row
      const cell = ws[addr];
      if (cell) cell.l = { Target: url, Tooltip: "Open in Google Maps" };
    });
  });
  return ws;
}

export default function Reports() {
  const [offline, setOffline] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [loading, setLoading] = useState(false);
  // Region drill-down. Both worklists carry `region` per row, so we slice client-side
  // and both the on-screen tables AND the Excel exports honour the selected region.
  const [regionFilter, setRegionFilter] = useState();
  // Bulk raise + assign OFFLINE tickets.
  const [assignees, setAssignees] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [assignee, setAssignee] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [off, fresh] = await Promise.all([
        getOfflineDevicesReport(),
        getImageFreshnessReport(),
      ]);
      setOffline(off);
      setFreshness(fresh);
    } catch (error) {
      notify.error("Failed to load reports", serverError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getAssignableFieldUsers()
      .then(setAssignees)
      .catch(() => setAssignees([]));
  }, [load]);

  const raiseAndAssign = useCallback(async () => {
    if (!assignee || selectedRowKeys.length === 0) return;
    setAssigning(true);
    try {
      const res = await assignOfflineTickets(selectedRowKeys, assignee);
      notify.success(
        "Tickets assigned",
        `Raised ${res.created}, reassigned ${res.reassigned} → ${res.assignedToName}`
      );
      setSelectedRowKeys([]);
      await load(); // refresh so the Existing-ticket column reflects the new assignments
    } catch (error) {
      notify.error("Failed to raise & assign tickets", serverError(error));
    } finally {
      setAssigning(false);
    }
  }, [assignee, selectedRowKeys, load]);

  // Distinct territories across both worklists, for the region dropdown.
  const regionOptions = useMemo(() => {
    const set = new Set();
    (freshness?.devices || []).forEach((d) => d.region && set.add(d.region));
    (offline?.rows || []).forEach((r) => r.region && set.add(r.region));
    return [...set]
      .sort((a, b) => a.localeCompare(b))
      .map((r) => ({ label: r, value: r }));
  }, [freshness, offline]);

  const inRegion = useCallback(
    (row) => !regionFilter || row.region === regionFilter,
    [regionFilter]
  );

  const freshnessDevices = useMemo(
    () => (freshness?.devices || []).filter(inRegion),
    [freshness, inRegion]
  );
  const offlineRows = useMemo(
    () => (offline?.rows || []).filter(inRegion),
    [offline, inRegion]
  );

  // Freshness headline tiles recomputed for the selected region from each device's
  // bucket (server buckets: "<=24h","24-48h",">48h","Never"). >24h and >48h are
  // cumulative and both include never-captured, matching the server summary.
  const freshnessSummary = useMemo(() => {
    if (!freshness) return null;
    if (!regionFilter) return freshness.summary;
    const s = { totalDeployed: 0, within24h: 0, over24h: 0, over48h: 0, neverCaptured: 0 };
    freshnessDevices.forEach((d) => {
      s.totalDeployed += 1;
      if (d.bucket === "<=24h") s.within24h += 1;
      else s.over24h += 1;
      if (d.bucket === ">48h" || d.bucket === "Never") s.over48h += 1;
      if (d.bucket === "Never") s.neverCaptured += 1;
    });
    return s;
  }, [freshness, regionFilter, freshnessDevices]);

  const offlineMissingCoordinates = useMemo(
    () => offlineRows.filter((r) => !r.hasCoordinates).length,
    [offlineRows]
  );

  const exportOffline = () => {
    if (!offlineRows.length) return notify.info("No offline devices to export.");
    const columns = [
      { header: "Store", key: "store", width: 28 },
      { header: "Store Code", key: "storeCode", width: 14 },
      { header: "Region", key: "region", width: 14 },
      { header: "Zone", key: "zone", width: 12 },
      { header: "City", key: "city", width: 14 },
      { header: "Channel", key: "channel", width: 16 },
      { header: "MR Supervisor (ASM)", key: "supervisor", width: 20 },
      { header: "Merchandiser", key: "merchandiser", width: 20 },
      { header: "Distributor", key: "distributor", width: 24 },
      { header: "Device ID", key: "deviceId", width: 14 },
      { header: "Device Title", key: "deviceTitle", width: 18 },
      { header: "Latitude", key: "lat", width: 11 },
      { header: "Longitude", key: "lng", width: 11 },
      { header: "Location", key: "location", width: 14, link: (r) => r.mapsUrl },
      { header: "Offline Since", key: "offlineSince", width: 20 },
      { header: "Last Seen", key: "lastSeenAt", width: 20 },
      { header: "Last Image", key: "lastImageAt", width: 20 },
      { header: "Reason", key: "reason", width: 40 },
    ];
    const rows = offlineRows.map((r) => ({
      ...r,
      location: r.mapsUrl ? "Open map" : "No GPS",
      offlineSince: toDate(r.offlineSince),
      lastSeenAt: toDate(r.lastSeenAt),
      lastImageAt: toDate(r.lastImageAt),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheetFrom(columns, rows), "Offline by Store");
    XLSX.writeFile(wb, `offline-devices_${stamp(offline.generatedAt)}.xlsx`, {
      cellDates: true,
    });
    notify.toast("Offline devices exported.");
  };

  const exportFreshness = () => {
    if (!freshnessSummary) return notify.info("No data to export.");
    const s = freshnessSummary;
    const summaryRows = [
      { metric: "Total Devices Deployed", value: s.totalDeployed },
      { metric: "Last Image <= 24 Hrs", value: s.within24h },
      { metric: "Last Image > 24 Hrs (cumulative)", value: s.over24h },
      { metric: "Last Image > 48 Hrs (cumulative)", value: s.over48h },
      { metric: "Never Captured", value: s.neverCaptured },
      { metric: "Generated At", value: toDate(freshness.generatedAt) },
    ];
    const summaryWs = sheetFrom(
      [
        { header: "Metric", key: "metric", width: 34 },
        { header: "Value", key: "value", width: 22 },
      ],
      summaryRows
    );
    const deviceCols = [
      { header: "Device ID", key: "deviceId", width: 14 },
      { header: "Device Title", key: "deviceTitle", width: 18 },
      { header: "Store", key: "store", width: 26 },
      { header: "Store Code", key: "storeCode", width: 14 },
      { header: "Region", key: "region", width: 14 },
      { header: "Zone", key: "zone", width: 12 },
      { header: "MR Supervisor (ASM)", key: "supervisor", width: 20 },
      { header: "Merchandiser", key: "merchandiser", width: 20 },
      { header: "Distributor", key: "distributor", width: 24 },
      { header: "Location", key: "location", width: 14, link: (r) => r.mapsUrl },
      { header: "Last Image", key: "lastImageAt", width: 20 },
      { header: "Age (hours)", key: "ageHours", width: 12 },
      { header: "Bucket", key: "bucket", width: 12 },
    ];
    const deviceRows = freshnessDevices.map((d) => ({
      ...d,
      location: d.mapsUrl ? "Open map" : "No GPS",
      lastImageAt: toDate(d.lastImageAt),
      ageHours: d.ageHours == null ? "" : d.ageHours,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
    XLSX.utils.book_append_sheet(wb, sheetFrom(deviceCols, deviceRows), "Devices");
    XLSX.writeFile(wb, `image-freshness_${stamp(freshness.generatedAt)}.xlsx`, {
      cellDates: true,
    });
    notify.toast("Image freshness exported.");
  };

  const bucketColor = { "<=24h": "green", "24-48h": "orange", ">48h": "red", Never: "red" };

  // Outlet + distribution owners (territory, ASM supervisor, merchandiser).
  const distributionCell = (_, r) => {
    if (!r.store) return <Text type="secondary">Unmapped</Text>;
    const territory = [r.region, r.zone].filter(Boolean).join(" › ");
    return (
      <div style={{ lineHeight: 1.35 }}>
        <div>
          <Text strong style={{ fontSize: 13 }}>{r.store}</Text>
          {r.storeCode && <Text type="secondary" style={{ fontSize: 12 }}> ({r.storeCode})</Text>}
        </div>
        {territory && <div><Text type="secondary" style={{ fontSize: 12 }}>{territory}</Text></div>}
        {r.supervisor && <div><Text type="secondary" style={{ fontSize: 12 }}>Supervisor: {r.supervisor}</Text></div>}
        {r.merchandiser && <div><Text type="secondary" style={{ fontSize: 12 }}>MR: {r.merchandiser}</Text></div>}
        {r.distributor && <div><Text type="secondary" style={{ fontSize: 12 }}>Distributor: {r.distributor}</Text></div>}
      </div>
    );
  };

  // Clickable Google Maps link where the outlet has captured GPS, else a flag.
  const locationCell = (_, r) =>
    r.mapsUrl ? (
      <a href={r.mapsUrl} target="_blank" rel="noreferrer">
        <EnvironmentOutlined /> Map
      </a>
    ) : (
      <Tag color="warning">No GPS</Tag>
    );

  // Existing open ticket for an offline device (raised earlier / by someone else).
  const existingTicketCell = (_, r) => {
    const t = r.openTicket;
    if (!t) return <Text type="secondary">—</Text>;
    return (
      <Space size={4} wrap>
        <Tag color={TICKET_STATUS_TAG_COLOR[t.status] || "default"}>
          {TICKET_STATUS_LABEL[t.status] || t.status}
        </Tag>
        {t.assignedToName && (
          <Text type="secondary" style={{ fontSize: 12 }}>→ {t.assignedToName}</Text>
        )}
      </Space>
    );
  };

  const offlineColumns = [
    { title: "Distribution", key: "distribution", render: distributionCell, width: 240 },
    { title: "Device", dataIndex: "deviceId", key: "deviceId" },
    { title: "Location", key: "location", render: locationCell },
    {
      title: "Offline for",
      key: "offlineFor",
      render: (_, r) => humanizeMins(r.offlineForMins) || fmt(r.offlineSince),
      sorter: (a, b) => (a.offlineForMins ?? -Infinity) - (b.offlineForMins ?? -Infinity),
    },
    { title: "Existing ticket", key: "existingTicket", render: existingTicketCell },
    { title: "Reason", dataIndex: "reason", key: "reason", ellipsis: true },
  ];

  const freshnessColumns = [
    { title: "Device", dataIndex: "deviceId", key: "deviceId" },
    { title: "Distribution", key: "distribution", render: distributionCell, width: 240 },
    { title: "Location", key: "location", render: locationCell },
    { title: "Last Image", dataIndex: "lastImageAt", key: "lastImageAt", render: fmt },
    {
      title: "Age (hrs)",
      dataIndex: "ageHours",
      key: "ageHours",
      render: (v) => (v == null ? "—" : v),
      sorter: (a, b) => (a.ageHours ?? Infinity) - (b.ageHours ?? Infinity),
    },
    {
      title: "Bucket",
      dataIndex: "bucket",
      key: "bucket",
      render: (v) => <Tag color={bucketColor[v] || "default"}>{v}</Tag>,
      filters: [
        { text: "<=24h", value: "<=24h" },
        { text: "24-48h", value: "24-48h" },
        { text: ">48h", value: ">48h" },
        { text: "Never", value: "Never" },
      ],
      onFilter: (val, r) => r.bucket === val,
    },
  ];

  const s = freshnessSummary || {};

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Exportable field-ops worklists for MR supervisors"
        tooltip="Store-wise offline devices (with coordinates) and last-image freshness across the deployed fleet. Export each as an Excel workbook."
        extra={
          <Space wrap>
            <Select
              showSearch
              allowClear
              placeholder="Filter by region"
              style={{ minWidth: 200 }}
              value={regionFilter}
              onChange={setRegionFilter}
              options={regionOptions}
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      />

      <Card
        style={{ marginBottom: 20, borderRadius: 12 }}
        title={
          <Space>
            <PictureOutlined />
            Image Freshness (deployed fleet)
          </Space>
        }
        extra={
          <Button type="primary" icon={<FileExcelOutlined />} onClick={exportFreshness} disabled={!freshness}>
            Export to Excel
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <StatCard title="Total Deployed" value={s.totalDeployed ?? "—"} icon={<WifiOutlined />} accent="#2f54eb" loading={loading} />
          </Col>
          <Col xs={12} md={6}>
            <StatCard title="Last Image ≤24h" value={s.within24h ?? "—"} icon={<ClockCircleOutlined />} accent="#52c41a" loading={loading} />
          </Col>
          <Col xs={12} md={6}>
            <StatCard title="Last Image >24h" value={s.over24h ?? "—"} icon={<ClockCircleOutlined />} accent="#faad14" loading={loading} tooltip="Everything older than 24 hours (includes the >48h bucket)." />
          </Col>
          <Col xs={12} md={6}>
            <StatCard title="Last Image >48h" value={s.over48h ?? "—"} icon={<ClockCircleOutlined />} accent="#f5222d" loading={loading} tooltip="Subset of >24h: no image for more than 48 hours (includes never-captured)." />
          </Col>
        </Row>
        <Table
          size="small"
          rowKey="deviceId"
          loading={loading}
          columns={freshnessColumns}
          dataSource={freshnessDevices}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: true }}
        />
      </Card>

      <Card
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <EnvironmentOutlined />
            Offline Devices by Store
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              showSearch
              allowClear
              value={assignee}
              onChange={setAssignee}
              options={assignees}
              placeholder="Assign to…"
              style={{ minWidth: 220 }}
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={raiseAndAssign}
              loading={assigning}
              disabled={!assignee || selectedRowKeys.length === 0}
            >
              Raise &amp; Assign{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ""}
            </Button>
            <Button icon={<FileExcelOutlined />} onClick={exportOffline} disabled={!offlineRows.length}>
              Export to Excel
            </Button>
          </Space>
        }
      >
        {offlineMissingCoordinates > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${offlineMissingCoordinates} of ${offlineRows.length} offline devices have no outlet coordinates.`}
            description="These devices are linked to an outlet with no lat/long, or not mapped to an outlet at all. Set the outlet's coordinates under Outlets to include them in the field worklist."
          />
        )}
        <Table
          size="small"
          rowKey="deviceId"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          columns={offlineColumns}
          dataSource={offlineRows}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
}
